/**
 * CreateTripPage - Dedicated full-screen page for creating new trips
 * Matches NewTripCreation.html design
 */

import { useNavigate } from 'react-router-dom';
import { useTrips } from '../contexts/TripContext';
import { useToast } from '../contexts/ToastContext';
import TripForm from '../components/trips/TripForm';
import DesktopSidebar from '../components/common/DesktopSidebar';
import type { CreateTripInput } from '../types/trip';

export default function CreateTripPage() {
  const navigate = useNavigate();
  const { createTrip } = useTrips();
  const { showToast } = useToast();

  const handleSubmit = async (data: CreateTripInput) => {
    try {
      await createTrip(data);
      showToast('success', '旅程已成功創建');
      navigate('/trips');
    } catch (err) {
      console.error('Failed to create trip:', err);
      showToast('error', err instanceof Error ? err.message : '創建旅程時發生錯誤');
    }
  };

  const handleCancel = () => {
    navigate('/trips');
  };

  return (
    <>
      {/* Mobile layout */}
      <div className="lg:hidden">
        <TripForm onSubmit={handleSubmit} onCancel={handleCancel} />
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:flex h-screen w-full overflow-hidden">
        <DesktopSidebar onNavigate={navigate} />

        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Top bar */}
          <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-slate-100 bg-white px-10">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[22px] font-bold text-slate-900">建立新旅程</h1>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="flex h-10 items-center rounded-lg border border-slate-200 px-5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              取消
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-1 items-center justify-center overflow-y-auto bg-[#f5f8f8] p-10">
            <div className="flex w-full max-w-[960px] overflow-hidden rounded-2xl bg-white shadow-lg">
              {/* Left hero panel */}
              <div className="relative w-[400px] shrink-0 overflow-hidden">
                <img
                  alt="山景與夕陽的旅行風景"
                  className="h-full w-full object-cover"
                  src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=900&fit=crop"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/53 to-transparent"></div>
                <div className="absolute bottom-8 left-8 text-white">
                  <p className="text-xs font-semibold uppercase tracking-widest opacity-90">開始規劃</p>
                  <h2 className="mt-1 text-2xl font-bold leading-snug">規劃新的冒險旅程</h2>
                </div>
              </div>

              {/* Right form panel */}
              <div className="flex-1 overflow-y-auto">
                <TripForm onSubmit={handleSubmit} onCancel={handleCancel} desktopMode />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
